import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, AlertTriangle, Wrench, Send, CheckCircle2, RotateCcw, MessageSquare, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi, fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, Field, Empty } from "@/components/kit";
import { PhotoPicker, Photos } from "@/components/PhotoPicker";

const TYPES = ["temperature", "contamination", "rejected_delivery", "pest", "illness", "allergen", "equipment", "injury", "complaint", "other"];

function NewIncident({ open, onClose, onSaved }) {
  const { venueId } = useAuth();
  const [f, setF] = useState({ type: "other", severity: "medium", title: "", description: "", photo_ids: [] });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/incidents", { ...f, venue_id: venueId });
      toast.success("Incident reported — managers alerted");
      setF({ type: "other", severity: "medium", title: "", description: "", photo_ids: [] });
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Report an incident</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><select data-testid="incident-type-select" className="ka-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}</select></Field>
            <Field label="Severity"><select data-testid="incident-severity-select" className="ka-input" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}>{["low", "medium", "high"].map((t) => <option key={t}>{t}</option>)}</select></Field>
          </div>
          <Field label="Title *"><input data-testid="incident-title-input" required minLength={3} className="ka-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="What happened? *"><textarea data-testid="incident-description-input" required minLength={3} rows={4} className="ka-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <Field label="Photos"><PhotoPicker testid="incident-new-photo" value={f.photo_ids} onChange={(v) => setF({ ...f, photo_ids: v })} /></Field>
          <button data-testid="submit-incident-button" className="ka-press w-full rounded-full bg-rose-600 py-3 text-sm font-semibold text-white hover:bg-rose-700">Report incident</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Actions({ inc, onUpdated }) {
  const { can } = useAuth();
  const [mode, setMode] = useState(null);
  const [f, setF] = useState({ text: "", corrective_action: inc.corrective_action || "", food_disposition: inc.food_disposition || "", owner_name: "", maintenance_status: "in_progress", photo_ids: [] });
  const send = async (type) => {
    try {
      const { data } = await api.post(`/incidents/${inc.id}/events`, { type, ...f });
      toast.success("Updated");
      setMode(null);
      setF((x) => ({ ...x, text: "", photo_ids: [] }));
      onUpdated(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const closed = inc.status === "closed";
  const btn = (key, label, Icon, perm, show = true) => show && can(perm) && (
    <button key={key} data-testid={`incident-action-${key}`} onClick={() => (["submit_review"].includes(key) ? send(key) : setMode(key))}
      className={`ka-press flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold ${mode === key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white hover:border-slate-900"}`}><Icon className="h-3.5 w-3.5" />{label}</button>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {btn("action", "Record corrective action", AlertTriangle, "record", !closed)}
        {btn("submit_review", "Submit for review", Send, "record", !closed && inc.status !== "awaiting_review")}
        {btn("approve", "Approve & close", CheckCircle2, "manage", inc.status === "awaiting_review")}
        {btn("assign", "Assign owner", UserPlus, "manage", !closed)}
        {btn("maintenance", "Raise maintenance job", Wrench, "manage", !closed && !inc.maintenance)}
        {btn("maintenance_update", "Update maintenance", Wrench, "manage", !closed && !!inc.maintenance)}
        {btn("comment", "Comment", MessageSquare, "record")}
        {btn("reopen", "Reopen", RotateCcw, "manage", closed)}
      </div>
      {mode && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 ka-fade">
          {mode === "action" && <>
            <Field label="Corrective action *"><textarea data-testid="incident-corrective-input" rows={2} className="ka-input" value={f.corrective_action} onChange={(e) => setF({ ...f, corrective_action: e.target.value })} /></Field>
            <Field label="Food disposition / safe storage decision *"><textarea data-testid="incident-food-input" rows={2} className="ka-input" value={f.food_disposition} onChange={(e) => setF({ ...f, food_disposition: e.target.value })} /></Field>
          </>}
          {mode === "assign" && <Field label="Owner"><input data-testid="incident-owner-input" className="ka-input" value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} /></Field>}
          {mode === "maintenance" && <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">A maintenance job is <b>not</b> a food safety corrective action. You still need to record what happened to the food.</p>}
          {mode === "maintenance_update" && <Field label="Maintenance status"><select data-testid="incident-maint-status" className="ka-input" value={f.maintenance_status} onChange={(e) => setF({ ...f, maintenance_status: e.target.value })}><option value="requested">Requested</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select></Field>}
          {mode !== "action" && mode !== "assign" && (
            <Field label={mode === "approve" ? "Safe-to-resume verification *" : "Notes"}><textarea data-testid="incident-text-input" rows={2} className="ka-input" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder={mode === "approve" ? "e.g. Fridge re-checked at 3.4°C for 2 consecutive readings" : ""} /></Field>
          )}
          {["action", "comment", "approve", "maintenance_update"].includes(mode) && <Field label="Photo evidence"><PhotoPicker testid="incident-event-photo" value={f.photo_ids} onChange={(v) => setF({ ...f, photo_ids: v })} /></Field>}
          <button data-testid="incident-action-confirm" onClick={() => send(mode)} className="ka-press rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Save</button>
        </div>
      )}
    </div>
  );
}

function Detail({ inc, onUpdated }) {
  return (
    <div className="mt-6 space-y-6">
      <div className="flex flex-wrap gap-2"><Pill status={inc.status} testid="incident-detail-status" /><Pill status={inc.severity} /><Pill status="low" label={inc.type.replace("_", " ")} /></div>
      <p className="text-sm text-slate-700">{inc.description}</p>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 p-3"><div className="ka-eyebrow">Corrective action</div><div className="mt-1">{inc.corrective_action || "—"}</div></div>
        <div className="rounded-xl border border-slate-200 p-3"><div className="ka-eyebrow">Food disposition</div><div className="mt-1">{inc.food_disposition || "—"}</div></div>
        <div className="rounded-xl border border-slate-200 p-3"><div className="ka-eyebrow">Owner</div><div className="mt-1">{inc.owner_name || "Unassigned"}</div></div>
        <div className="rounded-xl border border-slate-200 p-3"><div className="ka-eyebrow">Maintenance</div><div data-testid="incident-maintenance" className="mt-1">{inc.maintenance ? `${inc.maintenance.ref} · ${inc.maintenance.status.replace("_", " ")}` : "None linked"}</div></div>
      </div>
      <Actions inc={inc} onUpdated={onUpdated} />
      <div>
        <div className="ka-eyebrow mb-3">Timeline (append-only)</div>
        <ol className="space-y-3 border-l-2 border-slate-200 pl-4">
          {inc.events.map((e, i) => <li key={i} className="text-sm"><div className="font-mono text-[11px] text-slate-400">{fmtDT(e.at)} · {e.by} · {e.type.replace("_", " ")}</div><div className="text-slate-700">{e.text}</div>{e.photo_ids?.length > 0 && <div className="mt-2"><Photos ids={e.photo_ids} size="h-14 w-14" /></div>}</li>)}
        </ol>
      </div>
    </div>
  );
}

export default function Incidents() {
  const { venueId, venue, can } = useAuth();
  const [status, setStatus] = useState("open");
  const { data, reload } = useApi(venueId ? "/incidents" : null, { venue_id: venueId, status: status === "all" ? undefined : status });
  const [sel, setSel] = useState(null);
  const [params, setParams] = useSearchParams();
  const deepId = params.get("id");
  useEffect(() => {
    if (!deepId) return;
    api.get(`/incidents/${deepId}`).then((r) => setSel(r.data)).catch(() => {
      toast.error("That incident could not be found", { id: "deeplink-missing" });
      setParams({}, { replace: true });
    });
  }, [deepId, setParams]);
  const closeSheet = () => {
    setSel(null);
    if (deepId) setParams({}, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Incidents & corrective actions" subtitle="Out-of-range readings, failed checks and rejected deliveries land here automatically."
        actions={can("record") && <button data-testid="report-incident-button" onClick={() => setCreating(true)} className="ka-press inline-flex items-center gap-1 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"><Plus className="h-4 w-4" />Report incident</button>} />
      <div className="mb-5 flex gap-2">
        {["open", "awaiting_review", "closed", "all"].map((s) => <button key={s} data-testid={`incident-filter-${s}`} onClick={() => setStatus(s)} className={`ka-press rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${status === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{s.replace("_", " ")}</button>)}
      </div>
      {data && !data.length && <Empty testid="incidents-empty" icon={CheckCircle2} title="Nothing here" text="No incidents match this filter." />}
      <div className="space-y-3">
        {(data || []).map((i) => (
          <button key={i.id} data-testid={`incident-row-${i.id}`} onClick={() => setSel(i)} className="ka-press ka-card flex w-full flex-wrap items-center gap-3 p-5 text-left hover:border-slate-400">
            <AlertTriangle className={`h-5 w-5 ${i.severity === "high" ? "text-rose-600" : "text-amber-500"}`} />
            <div className="min-w-0 flex-1"><div className="font-semibold text-slate-900">{i.title}</div><div className="text-xs text-slate-500">{fmtDT(i.created_at)} · {i.created_by_name}{i.maintenance ? ` · ${i.maintenance.ref}` : ""}</div></div>
            <Pill status={i.severity} /><Pill status={i.status} />
          </button>
        ))}
      </div>
      <NewIncident open={creating} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />
      <Sheet open={!!sel} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader><SheetTitle className="pr-6 font-display text-2xl">{sel?.title}</SheetTitle></SheetHeader>
          {sel && <Detail inc={sel} onUpdated={(d) => { setSel(d); reload(); }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
